import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { List, ListItemAvatar, ListItemButton, ListItemText } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { DirectoryLab } from './directory-browser-config';
import SupplierLogo from './supplier-logo.component';

type SupplierMapPlaceListProps = {
    /** The labs or clinics of one bubble; the dialog is closed while empty. */
    labs: DirectoryLab[];
    onClose: () => void;
    onSelect: (lab: DirectoryLab) => void;
};

/**
 * DD-3698: the labs or clinics behind a bubble that zooming cannot split — several
 * organizations at one address. Choosing one opens its profile, like clicking its pin.
 */
const SupplierMapPlaceList: FC<SupplierMapPlaceListProps> = ({ labs, onClose, onSelect }) => {
    const { t: translate } = useTranslation(['client-directory']);
    return (
        <DHDialog
            open={labs.length > 0}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            title={translate('client-directory:MAP_SAME_PLACE_TITLE', { count: labs.length })}
        >
            <List disablePadding data-test="supplier-map.place-list">
                {labs.map((lab) => (
                    <ListItemButton
                        key={lab._id}
                        onClick={() => {
                            onClose();
                            onSelect(lab);
                        }}
                        data-test={`supplier-map.place-${lab._id}`}
                    >
                        {/* The logo is as wide as MUI's avatar slot, so the gap is explicit. */}
                        <ListItemAvatar sx={{ minWidth: 0, mr: 1.5 }}>
                            <SupplierLogo name={lab.name} logoSignedUrl={lab.logoSignedUrl} />
                        </ListItemAvatar>
                        <ListItemText
                            primary={lab.name}
                            secondary={[lab.street, lab.city].filter(Boolean).join(', ')}
                        />
                    </ListItemButton>
                ))}
            </List>
        </DHDialog>
    );
};

export default SupplierMapPlaceList;
